import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { LEGAL_CONTENT } from '../lib/LegalContent';
import { Eye, EyeOff } from 'lucide-react';

const Login = () => {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('standard');
  const [coachCode, setCoachCode] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const { user, login, signup } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (user) {
      navigate('/feed');
    }
  }, [user, navigate]);

  // Legal Consent State
  const [agreedKvkk, setAgreedKvkk] = useState(false);
  const [agreedPrivacy, setAgreedPrivacy] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [legalModal, setLegalModal] = useState({ open: false, type: null });

  const [verificationSent, setVerificationSent] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!username || !password) {
      setError(t('auth.enterUsername') + ' and ' + t('auth.enterPassword'));
      return;
    }

    try {
      await login(username, password, rememberMe);
      navigate('/feed');
    } catch (err) {
      // Show specific error message from backend
      if (err.response?.status === 403 && err.response?.data?.detail === 'EMAIL_NOT_VERIFIED') {
        setError(t('auth.emailNotVerified', 'Email not verified. Please check your inbox.'));
        setShowResend(true);
      } else {
        const errorMessage = err.response?.data?.detail || err.message || 'Login failed';
        setError(errorMessage);
        setShowResend(false);
      }
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !username || !password || !firstName || !lastName || !confirmPassword) {
      setError('Please fill all fields');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!agreedKvkk || !agreedPrivacy || !agreedTerms) {
      setError('Lütfen tüm yasal metinleri onaylayın');
      return;
    }

    if (role === 'coach' && !coachCode) {
      setError('Coach code is required for coach signup');
      return;
    }

    try {
      const signupData = {
        email,
        username,
        password,
        password,
        role,
        display_name: `${firstName.trim()} ${lastName.trim()}`,
        ...(role === 'coach' && { coach_join_code: coachCode })
      };

      const response = await signup(signupData);

      if (response && response.verificationRequired) {
        setVerificationSent(true);
        // Don't navigate, show success message
      } else {
        navigate('/feed');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Signup failed. Please try again.');
    }
  };

  const handleResendVerification = async () => {
    try {
      if (!username) return; // Username field contains email or username. 
      // Ideally we need email. Login form uses username state for email/username input.
      // Since backend resend expects email, if user typed username, it might fail if we don't have email.
      // But in login form, label says "Username". 
      // The requirement says: “Tekrar doğrulama maili gönder” butonu göster (email alanındaki email’i kullanarak çağır)
      // Login form has one input: username. User might enter username OR email.
      // If they enter username, backend resend endpoint expects email. 
      // Backend resend endpoint: Body: { email }. 
      // If user logs in with username, we can't resend without asking for email.
      // Assume user entered email if it looks like one, or we might need to ask.
      // For now, let's send whatever is in the username field. If backend requires strictly email, user might need to enter email.

      await authAPI.resendVerification(username);
      setError('Verification email resent. Please check your inbox.');
      setShowResend(false);
    } catch (err) {
      setError('Failed to resend verification email.');
    }
  };

  if (verificationSent) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-black">
        <Card className="w-full max-w-md bg-[#1a1a1b] border-gray-800">
          <CardHeader className="text-center">
            <CardTitle className="text-white">Verification Email Sent</CardTitle>
            <CardDescription className="text-gray-400">
              We've sent a verification link to <strong>{email}</strong>.
              <br />
              Please check your inbox to activate your account.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              onClick={() => setVerificationSent(false)}
              variant="outline"
              className="w-full border-gray-700 text-white hover:bg-white/10"
            >
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: `
          radial-gradient(1200px 600px at top left, rgba(0,255,150,0.25), transparent 60%),
          radial-gradient(1000px 500px at top right, rgba(255,200,0,0.25), transparent 60%),
          radial-gradient(1200px 600px at bottom right, rgba(140,0,255,0.25), transparent 60%),
          radial-gradient(1000px 500px at bottom left, rgba(0,120,255,0.25), transparent 60%),
          #000000
        `
      }}
    >
      <Card className="w-full max-w-md bg-[#1a1a1b] border-gray-800">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img
              src="/snapp-login-logo.png"
              alt="Snapp Logo"
              className="h-32 w-auto"
            />
          </div>
          <CardDescription className="text-gray-400">{t('app.tagline')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-[#0a0a0b]">
              <TabsTrigger value="login">Login</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="login-username" className="text-white">{t('auth.username')}</Label>
                  <Input
                    id="login-username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder={t('auth.enterUsername')}
                    className="bg-[#0a0a0b] border-gray-700 text-white placeholder:text-gray-500"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password" sledge-id="login-password-label" className="text-white">{t('auth.password')}</Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showLoginPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('auth.enterPassword')}
                      className="bg-[#0a0a0b] border-gray-700 text-white pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                    >
                      {showLoginPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center space-x-2 pb-2">
                  <Checkbox
                    id="rememberMe"
                    checked={rememberMe}
                    onCheckedChange={(checked) => setRememberMe(checked)}
                    className="border-gray-600 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                  />
                  <Label
                    htmlFor="rememberMe"
                    className="text-sm font-medium leading-none text-gray-300 cursor-pointer"
                  >
                    Beni Hatırla
                  </Label>
                </div>

                {error && (
                  <p className="text-sm text-red-500">{error}</p>
                )}
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                  {t('auth.signIn')}
                </Button>
                {showResend && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full mt-2 border-red-800 text-red-500 hover:bg-red-900/20"
                    onClick={handleResendVerification}
                  >
                    Resend Verification Email
                  </Button>
                )}

              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleSignup} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-role" className="text-white">Role</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger className="bg-[#0a0a0b] border-gray-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="coach">Coach</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    Standard users can browse the league and join teams. Once you join a team, you become a Player.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-firstname" className="text-white">First Name</Label>
                    <Input
                      id="signup-firstname"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="First Name"
                      className="bg-[#0a0a0b] border-gray-700 text-white placeholder:text-gray-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-lastname" className="text-white">Last Name</Label>
                    <Input
                      id="signup-lastname"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Last Name"
                      className="bg-[#0a0a0b] border-gray-700 text-white placeholder:text-gray-500"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-email" className="text-white">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="bg-[#0a0a0b] border-gray-700 text-white placeholder:text-gray-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-username" className="text-white">{t('auth.username')}</Label>
                  <Input
                    id="signup-username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder={t('auth.enterUsername')}
                    className="bg-[#0a0a0b] border-gray-700 text-white placeholder:text-gray-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-password" sledge-id="signup-password-label" className="text-white">{t('auth.password')}</Label>
                  <div className="relative">
                    <Input
                      id="signup-password"
                      type={showSignupPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('auth.enterPassword')}
                      className="bg-[#0a0a0b] border-gray-700 text-white placeholder:text-gray-500 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSignupPassword(!showSignupPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                    >
                      {showSignupPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-confirm-password" sledge-id="signup-confirm-password-label" className="text-white">Confirm Password</Label>
                  <div className="relative">
                    <Input
                      id="signup-confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm your password"
                      className="bg-[#0a0a0b] border-gray-700 text-white placeholder:text-gray-500 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {role === 'coach' && (
                  <div className="space-y-2">
                    <Label htmlFor="coach-code" className="text-white">Coach Code</Label>
                    <Input
                      id="coach-code"
                      type="text"
                      value={coachCode}
                      onChange={(e) => setCoachCode(e.target.value)}
                      placeholder="Enter coach code (e.g., TEAM-XY5K9)"
                      className="bg-[#0a0a0b] border-gray-700 text-white placeholder:text-gray-500"
                    />
                    <p className="text-xs text-gray-500">Required for coach signup</p>
                  </div>
                )}

                <div className="space-y-3 pt-2">
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="agreed-kvkk"
                      checked={agreedKvkk}
                      onCheckedChange={setAgreedKvkk}
                      className="mt-1 border-gray-600 data-[state=checked]:bg-blue-600"
                    />
                    <Label htmlFor="agreed-kvkk" className="text-xs text-gray-400 leading-relaxed cursor-pointer">
                      <button
                        type="button"
                        onClick={() => setLegalModal({ open: true, type: 'KVKK' })}
                        className="text-blue-500 hover:underline text-left"
                      >
                        KVKK Aydınlatma Metni'ni
                      </button> okudum ve anladım.
                    </Label>
                  </div>

                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="agreed-privacy"
                      checked={agreedPrivacy}
                      onCheckedChange={setAgreedPrivacy}
                      className="mt-1 border-gray-600 data-[state=checked]:bg-blue-600"
                    />
                    <Label htmlFor="agreed-privacy" className="text-xs text-gray-400 leading-relaxed cursor-pointer">
                      <button
                        type="button"
                        onClick={() => setLegalModal({ open: true, type: 'PRIVACY' })}
                        className="text-blue-500 hover:underline text-left"
                      >
                        Gizlilik Politikası'nı
                      </button> kabul ediyorum.
                    </Label>
                  </div>

                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="agreed-terms"
                      checked={agreedTerms}
                      onCheckedChange={setAgreedTerms}
                      className="mt-1 border-gray-600 data-[state=checked]:bg-blue-600"
                    />
                    <Label htmlFor="agreed-terms" className="text-xs text-gray-400 leading-relaxed cursor-pointer">
                      <button
                        type="button"
                        onClick={() => setLegalModal({ open: true, type: 'TERMS' })}
                        className="text-blue-500 hover:underline text-left"
                      >
                        Kullanım Şartları'nı
                      </button> onaylıyorum.
                    </Label>
                  </div>
                </div>

                {error && (
                  <p className="text-sm text-red-500">{error}</p>
                )}
                <Button
                  type="submit"
                  disabled={!agreedKvkk || !agreedPrivacy || !agreedTerms}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Sign Up
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Dialog open={legalModal.open} onOpenChange={(open) => setLegalModal(p => ({ ...p, open }))}>
        <DialogContent className="bg-[#1a1a1b] border-gray-800 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {legalModal.type && LEGAL_CONTENT[legalModal.type].title}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed py-4">
            {legalModal.type && LEGAL_CONTENT[legalModal.type].content}
          </div>
        </DialogContent>
      </Dialog>
    </div >
  );
};

export default Login;
